'use strict';

import {publishEvent} from './utilities';

/**
 * Returns true if the element's type is either radio or checkbox.
 */
function _isCheckable(inputField) {
    return _isCheckbox(inputField) || _isRadio(inputField);
}

function _isCheckbox(inputField) {
    return inputField.type === 'checkbox';
}

function _isRadio(inputField) {
    return inputField.type === 'radio';
}

/**
 * Populates the input/select/textarea element values for a specified `formEl`, using the provided `addressObject`.
 * Pass the `clearFields` argument to reset the field values.
 */
export function populateAddressFields(formEl, addressObject = {}, clearFields) {
    let addressFieldsMap = getAddressFieldsMap(formEl);

    for (let key in addressFieldsMap) {
        if (addressFieldsMap[key]) {
            //Some field values might be an object with a `value` property. Use that property if available.
            let newValue = addressObject[key] && typeof addressObject[key] === 'object' ? addressObject[key].value : addressObject[key];

            if (addressFieldsMap[key].fields && addressFieldsMap[key].fields.length) {
                [].forEach.call(addressFieldsMap[key].fields, function(currentField) {
                    if (addressFieldsMap[key].matchingAttribute && currentField[addressFieldsMap[key].matchingAttribute] === newValue) {
                        _setNewValue(key, currentField, newValue);
                    }
                });
            } else {
                _setNewValue(key, addressFieldsMap[key], newValue);
            }
        }
    }

    function _setNewValue(key, currentField, newValue) {
        if (addressObject.hasOwnProperty(key) || clearFields) {
            if (_isCheckable(currentField)) {
                currentField.checked = newValue;
            } else {
                currentField.value = newValue || '';
            }

            //Fire an "external change" event that can be hooked into from other places.
            //This prevents firing the OOTB "change" event, since parts of the application might be listening to those.
            publishEvent('external', 'change', currentField);
        }
    }
}

/**
 * Returns an object containing key:<value> pairs of matching elements withing the provided `formEl`,
 * where <value> equals to the matching HTMLElement.
 */
export function getAddressFieldsMap(formEl) {
    return {
        salutation: {
            matchingAttribute: 'value',
            fields: formEl.querySelectorAll('input[name$=_salutation]')
        },
        honoraryTitle: formEl.querySelector('input[name$=_honoraryTitle]'),
        firstName: formEl.querySelector('input[name$=_firstName]'),
        lastName: formEl.querySelector('input[name$=_lastName]'),
        customFirstName: formEl.querySelector('input[name$=_customnames_firstname]'),
        customLastName: formEl.querySelector('input[name$=_customnames_lastname]'),
        address1: formEl.querySelector('input[name$=_address1]'),
        address2: formEl.querySelector('input[name$=_address2]'),
        companyName: formEl.querySelector('input[name$=companyName]'),
        city: formEl.querySelector('select[name$=_cityCode], input[name$=_cityCode]'),
        postalCode: formEl.querySelector('input[name$=_postalCode]:not([disabled])'),
        stateCode: formEl.querySelector('select[name$=_stateCode]:not([disabled]), input[name$=_stateCode]:not([disabled])'),
        countryCode: formEl.querySelector('select[name$=_countryCode], select[name$=_country]'),
        phone: formEl.querySelector('input[name$=_phone]'),
        isGift: formEl.querySelector('input[name$=_isGift]'),
        giftMessage: formEl.querySelector('textarea[name$=_giftMessage]'),
        usingVerifiedAddress: formEl.querySelector('input[name$=_usingVerifiedAddress]'),
        email: formEl.querySelector('input[name$=_email]'),
        district: formEl.querySelector('input[name$=_district]'),
        streetnumber: formEl.querySelector('input[name$=_streetnumber]')
    };
}

/**
 * Returns an object containing key:<value> pairs of matching elements withing the provided `formEl`,
 * where <value> equals the actual value or state (in the case of a "checkable" element) of the matching element.
 * Leverages `getAddressFieldsMap` to gather the map data.
 */
export function getAddressFieldsFromUI(formEl) {
    let addressFieldsMap = getAddressFieldsMap(formEl),
        addressValuesMap = {};

    for (let key in addressFieldsMap) {
        if (addressFieldsMap[key]) {
            if (addressFieldsMap[key].fields && addressFieldsMap[key].fields.length) {
                [].forEach.call(addressFieldsMap[key].fields, function(currentField) {
                    if (_isRadio(currentField) && currentField.checked) {
                        addressValuesMap[key] = currentField[addressFieldsMap[key].matchingAttribute];
                    }
                });
            } else {
                addressValuesMap[key] = _isCheckable(addressFieldsMap[key]) ? addressFieldsMap[key].checked : addressFieldsMap[key].value;
            }
        }
    }

    return addressValuesMap;
}

/**
 * Returns an object with the user's Salutation value and displayValue.
 * Ideally, this should come from the backend directly, but it was set as String in the BM System Object, so here we are.
 */
export function getSalutationData(formDefinition) {
    return formDefinition ? {
        salutation: {
            value: formDefinition.salutation,
            displayValue: window.SFRA.Resources.salutations[formDefinition.salutation]
        }
    } : {};
}

export function getEffectiveCustomerNames(addressObj) {
    let firstName = addressObj.firstName || '',
        lastName = addressObj.lastName || '';

    if (addressObj.useCustomNames) {
        firstName = addressObj.customFirstName || firstName;
        lastName = addressObj.customLastName || lastName;
    }

    return `${firstName} ${lastName}`
}

export function constructAddressOptionLabel(addressObj, shippingMethod, isBilling) {
    return `${addressObj.salutation.displayValue || ''} ${getEffectiveCustomerNames(addressObj)} ${addressObj.address1 || ''} ${addressObj.address2 || ''} ${addressObj.city || ''} ${addressObj.stateCode || ''} ${addressObj.postalCode || ''} ${!isBilling && shippingMethod ? (' - ' + shippingMethod.displayName) : ''}`;
}
